import { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import i18next, { languages, changeAppLanguage } from "@plannel/utils/i18n";
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

import AccountBoxIcon from '@mui/icons-material/AccountBox';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChecklistIcon from '@mui/icons-material/Checklist';

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RouteList } from "@plannel/routeList";

import MenuSearch from "@plannel/components/MenuSearch";
import NotificationBell from "./NotificationBell";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import { FS, applyFontScale, getFontScale, fontScales } from "@plannel/utils/font-util";

import userService from "@plannel/services/system/user-service";
import authService from "@plannel/services/system/auth-service";
import tabMenuList from "@plannel/pages/TabMenuList"
import CodeUtil from "@plannel/pages/utils/code-util";
import "@plannel/scss/material-ui/ToolBar.scss";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import { toggleAgentChat, getAgentChatOpen } from "@plannel/redux/modules/AgentChatState";
import { toggleWorkflowPanel, getWorkflowPanelOpen } from "@plannel/redux/modules/WorkflowPanelState";

const langFontSize = {
  fontSize: FS.H2,
};

const langKrTitle = (<i style={langFontSize}>한국어 </i>);
const langJpTitle = (<i style={langFontSize}>日本語 </i>);
const langEnTitle = (<i style={langFontSize}>English </i>);
const langZhTitle = (<i style={langFontSize}>中文(繁體) </i>);
const langCnTitle = (<i style={langFontSize}>中文(简体) </i>);
const langViTitle = (<i style={langFontSize}>Tiếng Việt </i>);

const getLangTitle = (lang) => {
  switch (lang) {
    case languages.KO_KR:
      return langKrTitle;
    case languages.JA_JP:
      return langJpTitle;
    case languages.ZH_TW:
      return langZhTitle;
    case languages.ZH_CN:
      return langCnTitle;
    case languages.VI_VN:
      return langViTitle;
    default:
      return langEnTitle;
  }
};

const CustomNavbar = ({ t, showedTabsInfo, selectedTabIdx, showLogo, handleAddTab, initTabInfo, permittedMenus }) => {
  const navigate = useNavigate();
  const settingsData = JSON.parse(localStorage.getItem("settings"));
  const userData = JSON.parse(localStorage.getItem("user"));
  const [logoImage, setLogoImage] = useState("");

  const reduxDispatch = useDispatch();

  const getLocalLangCode = () => {
    let lang = settingsData.lang;
    if (lang === null) {
      lang = "en";
    }
    return lang;
  }

  const retrieveUserInfo = () => {
    userService.getUser({ username: userData.username })
      .then(
        (response) => {
          const userInfo = response.data.results[0];
          const currentUserData = JSON.parse(localStorage.getItem("user"));
          currentUserData.postnName = userInfo.postnNm;
          currentUserData.siteName = userInfo.siteNm;
          currentUserData.mfa = userInfo.mfaFlg;
          localStorage.setItem("user", JSON.stringify(currentUserData));
        },
      )
      .catch(e => { debug(e); });
  }

  const [langCode, setLangCode] = useState(getLocalLangCode());
  const [fontScale, setFontScale] = useState(getFontScale);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPath, setMenuPath] = useState(null);
  const agentChatOpen = useSelector(getAgentChatOpen);
  const workflowPanelOpen = useSelector(getWorkflowPanelOpen);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  }

  const handleHomeClick = () => {
    initTabInfo();
    reduxDispatch(reduxUtil.initTabState());
    reduxDispatch(reduxUtil.initViewState());
    navigate(RouteList.Home.path, { state: { tabUpdate: true } });
  }

  const changeLanguage = (key) => {
    handleClose();
    changeAppLanguage(key);
    setLangCode(key);
  }

  // 폰트 크기 스케일 전환. 실제 크기는 App.scss 의 `:root[data-font-scale]` 이 결정하므로
  // 여기서는 속성만 바꾸면 전 화면이 따라온다(리렌더 불필요).
  const changeFontScale = (key) => {
    setFontScale(applyFontScale(key));
  }

  const logOut = () => {
    userService.logout()
      .catch(e => {
        debug(e);
      })
      .then(() => {
        authService.logout();
        reduxDispatch(reduxUtil.initTabState());
        navigate(RouteList.Signin.path);
      });
  }

  useEffect(() => {
    const setCompanyInfo = () => {
      if (!showLogo.data) {
        setLogoImage(<HomeIcon sx={{ mr: 0.5, mb: 0.5, mt: 1 }} fontSize="inherit" />);
      }
      else {
        if (!showLogo.data.name) {
          setLogoImage(<HomeIcon sx={{ mr: 0.5, mb: 0.5, mt: 1 }} fontSize="inherit" />);
        }
        else {
          if (!showLogo.data.ciImage) {
            setLogoImage(<Typography color="inherit" sx={{ fontSize: FS.KPI, fontWeight: "bold", pb: "3px" }}>{showLogo.data.name}</Typography>);
          } else {
            const imgsrc = `data:image/png;base64,${btoa(String.fromCharCode.apply(null, new Uint8Array(showLogo.data.ciImage)))}`;
            setLogoImage(<img style={{ height: '32px' }} src={imgsrc} alt=""></img>);
          }
        }
      }
    }

    if (showLogo) {
      setCompanyInfo();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLogo])

  /*
   * ★ 인덱스가 목록 범위를 벗어날 수 있다 — 저장된 탭 상태가 메뉴 변경보다 오래된 경우다.
   *   `selectedTabIdx !== -1` 만으로는 부족하다(범위 초과는 걸러지지 않는다). App 쪽에서 인덱스를
   *   clamp 하지만, 여기서 터지면 **앱 셸 전체가 에러 화면으로 대체되므로** 마지막 방어선을 둔다.
   */
  useEffect(() => {
    setMenuPath(showedTabsInfo?.[selectedTabIdx]?.menuPath ?? null);
  }, [showedTabsInfo, selectedTabIdx]);

  useEffect(() => {
    const handleLanguageChanged = () => {
      tabMenuList.rebuildArReportMenu();
      const path = showedTabsInfo?.[selectedTabIdx]?.menuPath;
      if (path) setMenuPath([...path]);
    };
    i18next.on('languageChanged', handleLanguageChanged);
    return () => i18next.off('languageChanged', handleLanguageChanged);
  }, [showedTabsInfo, selectedTabIdx]);

  useEffect(() => {
    const handleLanguageChanged = () => {
      tabMenuList.rebuildArReportMenu();
      if (showedTabsInfo && showedTabsInfo.length > 0 && selectedTabIdx !== -1) {
        setMenuPath([...showedTabsInfo[selectedTabIdx].menuPath]);
      }
    };
    i18next.on('languageChanged', handleLanguageChanged);
    return () => i18next.off('languageChanged', handleLanguageChanged);
  }, [showedTabsInfo, selectedTabIdx]);

  useEffect(() => {
    retrieveUserInfo();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const UserTooltipTitle = () => {
    return (
      <Typography variant="string">{`${t("user")}: ${userData.username}`} <br></br>{`${t("dept")}: ${userData.deptName ? userData.deptName : ""}`}<br></br>{`${t("postn")}: ${userData.postnName ? userData.postnName : ""}`}</Typography>
    );
  }

  return (
    <AppBar position="static" color="transparent" sx={{ borderBottom: 1, boxShadow: 0, borderColor: "grey.300" }} >
      <Toolbar variant="dense" sx={{ minHeight: "30px", mt: "1.5px", mb: "1.5px" }}>
        <Breadcrumbs edge="start" aria-label="breadcrumb" sx={{ mr: "auto", flex: "none" }} >
          <IconButton
            size="small"
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleHomeClick}
            sx={{ p: 0, pr: "2px", height: "32px" }}
          >
            {logoImage}
          </IconButton>
          {
            menuPath?.map((item) => {
              const displayTitle = item.skipI18n ? (tabMenuList.getLocalizedTitle(item.reduxKey) ?? item.menuTitle) : t(item.menuTitle);
              if (menuPath.length > 1) {
                return <Typography key={item.menuCd} color="inherit" sx={{ fontSize: FS.SUB, pt: 0.4, color: "black" }}>{displayTitle}</Typography>
              }
              if (item.singleMenu) {
                return <Typography key={item.menuCd} color="black" sx={{ fontSize: FS.SUB, paddingTop: 0.4 }}>{displayTitle}</Typography>
              }
              const menuInfos = tabMenuList.findLv3Menu(item.menuCd);
              const lv1Info = tabMenuList.getLv1MenuInfo(menuInfos?.lv2Key);
              const lv1KeyTitle = lv1Info?.title;
              return [
                lv1KeyTitle && <Typography key={menuInfos.lv2Key} color="black" sx={{ fontSize: FS.SUB, paddingTop: 0.4 }}>{t(lv1KeyTitle)}</Typography>,
                <Typography key={item.menuCd} color="black" sx={{ fontSize: FS.SUB, paddingTop: 0.4 }}>{displayTitle}</Typography>
              ]
            })
          }
        </Breadcrumbs>
        {/* 메뉴 검색 — 오른쪽 아이콘 묶음의 맨 앞. breadcrumb 이 mr:"auto" 로 왼쪽을 다 먹으므로
            여기가 "항상 보이면서 이름표를 안 밀어내는" 유일한 자리다. */}
        <MenuSearch menus={permittedMenus} onSelect={handleAddTab} />
        <Tooltip id="home" title={t("home")}>
          <IconButton aria-label="home" color="inherit" size="small" onClick={handleHomeClick} sx={{ mr: 0.5 }}>
            <HomeIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          id="lang-button"
          aria-controls="lang-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : 'undefined'}
          onClick={handleClick} color="inherit" sx={{ textTransform: "None" }}
        >
          {getLangTitle(langCode)}
        </Button>
        <Menu
          id="lang-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "lang-button",
          }}
        >
          <MenuItem onClick={() => changeLanguage(languages.EN_US)}>{langEnTitle}</MenuItem>
          <MenuItem onClick={() => changeLanguage(languages.KO_KR)}>{langKrTitle}</MenuItem>
          <MenuItem onClick={() => changeLanguage(languages.JA_JP)}>{langJpTitle}</MenuItem>
          <MenuItem onClick={() => changeLanguage(languages.ZH_TW)}>{langZhTitle}</MenuItem>
          <MenuItem onClick={() => changeLanguage(languages.ZH_CN)}>{langCnTitle}</MenuItem>
          <MenuItem onClick={() => changeLanguage(languages.VI_VN)}>{langViTitle}</MenuItem>
        </Menu>
        {/* 폰트 크기 스케일 — 배타 선택이라 SegmentedControl. Tooltip 이 ref 를 걸 수 있게 Box 로 감싼다. */}
        <Tooltip id="fontScale" title={t("fontScale")}>
          <Box sx={{ display: "inline-flex", mr: 1 }}>
            <SegmentedControl
              aria-label={t("fontScale")}
              value={fontScale}
              onChange={changeFontScale}
              options={[
                { value: fontScales.SMALL, label: t("fontScaleSmall") },
                { value: fontScales.BIG, label: t("fontScaleBig") },
              ]}
              /* 툴바(minHeight 30px)에 맞춰 압축. `&&` 로 특이도를 올려야 한다 —
                 같은 `& .MuiToggleButtonGroup-grouped` 키로 주면 sx 병합에서 원본 블록이
                 통째로 교체되어 색·선택 상태가 사라진다. */
              sx={{ "&& .MuiToggleButtonGroup-grouped": { fontSize: FS.SUB, px: 1.2, py: 0.25, lineHeight: 1.4 } }}
            />
          </Box>
        </Tooltip>
        <NotificationBell />
        {userData?.roles?.includes(CodeUtil.roles.APP_AI) && (
          <Tooltip title="PlanNEL AI">
            <IconButton color="inherit" size="small" onClick={() => reduxDispatch(toggleAgentChat())} sx={{ mr: 1, color: agentChatOpen ? "primary.main" : "inherit" }}>
              <SmartToyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip id="workflow" title={t("workflow")}>
          <IconButton aria-label="workflow" color="inherit" size="small" onClick={() => reduxDispatch(toggleWorkflowPanel())} sx={{ color: workflowPanelOpen ? "primary.main" : "inherit" }}>
            <ChecklistIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip id="userManagement" title={<UserTooltipTitle />}>
          <IconButton aria-label="user management" color="inherit" size="small" onClick={() => handleAddTab(400)}>
            <AccountBoxIcon />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: FS.H2 }}>{userData.username}</Typography>
        <Tooltip id="logout" title="Logout">
          <IconButton aria-label="logout" color="inherit" size="small" onClick={logOut}>
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  )
};

export default withTranslation()(CustomNavbar);